/**
 * Context provider for Google Calendar integration state and actions.
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { TierContext } from '@/features/tier/TierContext.def';
import {
  subscribeToGCalData,
  updateGCalEnabledPreference,
} from '@/services/gcalPreferenceService';
import { GCalContext } from './GCalContext.def';
import { gcalAuthService } from '@/features/planning/api/gcalAuthService';
import { gcalSyncService } from '@/services/gcalSyncService';

/**
 * Provides Google Calendar state (events, sync status) to the application.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const GCalProvider = ({ children }) => {
  const { user } = useAuth();
  const isFree = useContext(TierContext)?.isFree ?? false;
  const [isEnabled, setIsEnabled] = useState(false);
  const [events, setEvents] = useState([]);
  const [syncStatus, setSyncStatus] = useState({
    loading: false,
    lastSync: null,
    error: null,
  });

  // Load preferences and cached events from Firestore via service
  useEffect(() => {
    if (!user?.uid) return;

    const unsub = subscribeToGCalData(
      user.uid,
      (data) => {
        setIsEnabled(data.isEnabled);
        setEvents(data.events);
        setSyncStatus((prev) => ({
          ...prev,
          lastSync: data.lastSync,
          error: null,
        }));
      },
      (error) => {
        setSyncStatus((prev) => ({
          ...prev,
          error: error.message,
        }));
      },
    );

    return () => unsub();
  }, [user?.uid]);

  const triggerSync = useCallback(async () => {
    if (!user?.uid || !isEnabled || isFree) return;

    setSyncStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await gcalSyncService.syncGCalEvents(user.uid);
      // State is updated automatically via subscribeToGCalData onSnapshot
      setSyncStatus((prev) => ({ ...prev, loading: false }));
    } catch (error) {
      console.error('[GCalContext] Sync failed:', error);
      setSyncStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  }, [user?.uid, isEnabled, isFree]);

  useEffect(() => {
    if (!isEnabled || !user?.uid) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerSync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isEnabled, user?.uid, triggerSync]);

  const runInitialSync = useCallback(async (userId) => {
    setSyncStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await gcalSyncService.syncGCalEvents(userId);
      setSyncStatus((prev) => ({ ...prev, loading: false }));
    } catch (error) {
      console.error('[GCalContext] Initial sync failed:', error);
      setSyncStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  }, []);

  const toggleEnabled = useCallback(async () => {
    const newValue = !isEnabled;
    const userId = user?.uid;

    if (!userId) {
      console.warn('[GCalContext] No user UID available for toggle');
      return;
    }
    if (newValue && isFree) return;

    try {
      if (newValue) {
        await gcalAuthService.getToken(true);
      }

      await updateGCalEnabledPreference(userId, newValue);
      setIsEnabled(newValue);

      if (newValue) {
        await runInitialSync(userId);
      }
    } catch (error) {
      console.error(
        '[GCalContext] Failed to update GCal integration state:',
        error,
      );
      // We don't update local state if auth/db fails
    }
  }, [isEnabled, user?.uid, isFree, runInitialSync]);

  const getEventsForRange = useCallback(
    (startDate, endDate) => {
      if (!isEnabled) return [];
      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime();

      return events.filter((event) => {
        if (!event.start) return false;
        const eventStart = new Date(
          event.start.dateTime || event.start.date,
        ).getTime();
        return eventStart >= start && eventStart <= end;
      });
    },
    [events, isEnabled],
  );

  const value = useMemo(
    () => ({
      isEnabled,
      events,
      syncStatus,
      toggleEnabled,
      getEventsForRange,
      triggerSync,
    }),
    [
      isEnabled,
      events,
      syncStatus,
      toggleEnabled,
      getEventsForRange,
      triggerSync,
    ],
  );

  return <GCalContext.Provider value={value}>{children}</GCalContext.Provider>;
};
